<template>
  <!-- 没有可展示的就什么都不渲染 -->
  <div
    v-if="orderedVisibleBanners?.length"
    class="relative flex flex-col items-center"
  >
    <div class="relative h-[56px] w-full bg-[#0F172B] overflow-hidden">
      <transition name="banner-fade" mode="out-in">
        <div
          v-if="currentBanner"
          :key="currentBanner.id"
          class="w-full relative"
        >
          <!-- 内容本身 -->
          <div
            class="w-full mx-auto relative"
            id="banner-content"
            @click="handleBannerClick"
          >
            <div
              class="flex-1 text-center md:text-left bg-gray-800 rounded-lg shadow-lg p-0"
            >
              <div v-html="currentBanner.html"></div>
            </div>
          </div>

          <!-- 浮动 + 垂直居中的关闭按钮 -->
          <UButton
            icon="material-symbols-light:cancel-outline-rounded"
            size="lg"
            variant="ghost"
            color="white"
            class="absolute! right-4 top-1/2 -translate-y-1/2 z-9999 px-0"
            @click.stop="closeBanner(currentBanner.id)"
          />
        </div>
      </transition>

      <!-- 轮播指示器 - 底部居中 -->
      <div
        v-if="orderedVisibleBanners.length > 1"
        class="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 z-50"
      >
        <button
          v-for="(banner, index) in orderedVisibleBanners"
          :key="banner.id"
          :class="[
            'h-1.5 rounded-full transition-all duration-300 relative overflow-hidden',
            index === currentIndex
              ? 'bg-white/30 w-10'
              : 'bg-white/40 w-1.5 hover:bg-white/60',
          ]"
          @click="goToBanner(index)"
        >
          <!-- 倒计时进度条 -->
          <div
            v-if="index === currentIndex"
            :key="`progress-${currentIndex}-${progressKey}`"
            class="banner-progress-bar absolute inset-0 bg-white rounded-full origin-left"
            :style="{ animationDuration: `${ROTATE_INTERVAL}ms` }"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

const allBanners = ref([]);
const r2CachedFetch = useR2CachedFetch();
const dismissedMap = ref({}); // bannerId -> dismissTime ISO
const rotationOrder = ref([]); // 存储轮换顺序的数组
const currentIndex = ref(0); // 当前显示的 banner 索引
const progressKey = ref(0); // 用于触发动画重新开始
let autoRotateTimer = null; // 自动轮播定时器
const ROTATE_INTERVAL = 10000; // 10秒切换

const STORAGE_KEY = "top_banner_dismissed";

// 读取 localStorage
const loadDismissed = () => {
  if (process.server) return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    dismissedMap.value = raw ? JSON.parse(raw) : {};
  } catch {
    dismissedMap.value = {};
  }
};

// 写 localStorage
const saveDismissed = () => {
  if (process.server) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(dismissedMap.value)
    );
  } catch {
    // ignore
  }
};

// 当前时间（可以按需改成用服务器时间）
const now = () => new Date();

// 关闭某个 banner
const closeBanner = (id) => {
  dismissedMap.value[id] = new Date().toISOString();
  saveDismissed();

  // 从轮换顺序中移除
  const index = rotationOrder.value.indexOf(id);
  if (index > -1) {
    rotationOrder.value.splice(index, 1);
  }

  // 从列表中移除
  allBanners.value = allBanners.value.filter((b) => b.id !== id);

  // 如果关闭的是当前显示的 banner，重置索引
  if (currentBanner.value?.id === id) {
    currentIndex.value = 0;
  } else {
    // 调整索引，确保不越界
    const currentBannerIndex = orderedVisibleBanners.value.findIndex(
      (b) => b.id === currentBanner.value?.id
    );
    if (currentBannerIndex > -1) {
      currentIndex.value = currentBannerIndex;
    }
  }
};

// 过滤后的可见列表（不排序）
const visibleBanners = computed(() => {
  const t = now().getTime();
  return allBanners.value.filter((b) => {
    if (b.enabled === 0) return false;

    // 转换为UTC时间戳
    const start = new Date(b.start_at.replace(" ", "T") + "Z").getTime();
    const end = new Date(b.end_at.replace(" ", "T") + "Z").getTime();

    // 时间窗口外
    if (t < start || t > end) return false;

    // 如果用户已经关闭，并且仍在时间窗口内，则不再显示
    if (dismissedMap.value[b.id]) {
      return false;
    }

    return true;
  });
});

// 应用轮换顺序后的可见列表
const orderedVisibleBanners = computed(() => {
  // 如果没有轮换顺序，则按 updated_at 排序
  if (rotationOrder.value.length === 0) {
    return visibleBanners.value.sort(
      (a, b) =>
        new Date(b.updated_at.replace(" ", "T") + "Z").getTime() -
        new Date(a.updated_at.replace(" ", "T") + "Z").getTime()
    );
  }

  // 根据轮换顺序排序
  const bannersById = {};
  visibleBanners.value.forEach((banner) => {
    bannersById[banner.id] = banner;
  });

  // 按照 rotationOrder 的顺序返回 banner
  const result = [];
  rotationOrder.value.forEach((id) => {
    if (bannersById[id]) {
      result.push(bannersById[id]);
    }
  });

  // 添加不在 rotationOrder 中的新 banner（如果有）
  visibleBanners.value.forEach((banner) => {
    if (!rotationOrder.value.includes(banner.id)) {
      result.push(banner);
    }
  });

  return result;
});

// 当前显示的 banner
const currentBanner = computed(() => {
  if (orderedVisibleBanners.value.length === 0) return null;
  const index = currentIndex.value % orderedVisibleBanners.value.length;
  return orderedVisibleBanners.value[index];
});

const router = useRouter();

const handleBannerClick = (event) => {
  // 检查点击事件的源是否是你 Banner 中的按钮（类名为 'banner-btn'）
  const targetElement = event.target.closest(".banner-btn");

  // 如果点击的不是 banner 按钮，则不处理（去掉空白处切换功能）
  if (!targetElement) {
    return;
  }

  // ------------------------------------------------------------------
  // 阻止默认的链接跳转行为
  // ------------------------------------------------------------------
  event.preventDefault();

  // 获取目标 URL
  const href = targetElement.getAttribute("href");

  if (href) {
    try {
      // href 是没有host的, 需要提取path和query
      const baseURL = window.location.origin;

      // 构造完整的 URL 对象
      const urlObject = new URL(href, baseURL);
      router.push({
        path: urlObject.pathname,
        query: Object.fromEntries(new URLSearchParams(urlObject.search)),
      });
    } catch (e) {
      console.error("URL解析失败，执行标准跳转:", e);
      window.location.href = href;
    }
  }
};

// 跳转到指定索引的 banner
const goToBanner = (index) => {
  if (index >= 0 && index < orderedVisibleBanners.value.length) {
    currentIndex.value = index;
    resetAutoRotateTimer();
  }
};

// 切换到下一个 banner
const rotationBanner = () => {
  if (orderedVisibleBanners.value.length <= 1) return;
  currentIndex.value =
    (currentIndex.value + 1) % orderedVisibleBanners.value.length;
  resetAutoRotateTimer();
};

// 初始化轮换顺序
const initRotationOrder = () => {
  if (visibleBanners.value.length > 0 && rotationOrder.value.length === 0) {
    rotationOrder.value = visibleBanners.value
      .sort(
        (a, b) =>
          new Date(b.updated_at.replace(" ", "T") + "Z").getTime() -
          new Date(a.updated_at.replace(" ", "T") + "Z").getTime()
      )
      .map((b) => b.id);
  }
  // 重置当前索引
  currentIndex.value = 0;
};

// 启动自动轮播定时器
const startAutoRotate = () => {
  if (orderedVisibleBanners.value.length <= 1) return;
  clearAutoRotateTimer();
  // 触发进度条动画重新开始
  progressKey.value++;
  autoRotateTimer = setInterval(() => {
    rotationBanner();
  }, ROTATE_INTERVAL);
};

// 清除自动轮播定时器
const clearAutoRotateTimer = () => {
  if (autoRotateTimer) {
    clearInterval(autoRotateTimer);
    autoRotateTimer = null;
  }
};

// 重置自动轮播定时器（切换后重新计时）
const resetAutoRotateTimer = () => {
  clearAutoRotateTimer();
  if (orderedVisibleBanners.value.length > 1) {
    startAutoRotate();
  }
};

// 监听 visibleBanners 变化，初始化轮换顺序
watch(
  visibleBanners,
  () => {
    initRotationOrder();
  },
  { immediate: true }
);

// 监听 orderedVisibleBanners 变化，启动或停止自动轮播
watch(
  orderedVisibleBanners,
  (newBanners) => {
    if (newBanners.length > 1) {
      startAutoRotate();
    } else {
      clearAutoRotateTimer();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  loadDismissed();
  const data = await r2CachedFetch("/api/r2/pop/banners.json");
  if (data) {
    allBanners.value = data;
  }
});

onUnmounted(() => {
  clearAutoRotateTimer();
});
</script>

<style scoped>
.banner-fade-enter-active,
.banner-fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.banner-fade-enter-from,
.banner-fade-leave-to {
  opacity: 0;
}

/* 强制覆盖 banner HTML 内容中的响应式高度设置 */
:deep(.promo-banner) {
  min-height: 56px !important;
  height: 56px !important;
}

@media (max-width: 768px) {
  :deep(.promo-banner) {
    min-height: 56px !important;
    height: 56px !important;
  }
}

/* 倒计时进度条动画 */
.banner-progress-bar {
  animation: progress-countdown linear forwards;
  will-change: transform;
}

@keyframes progress-countdown {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
</style>
